import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  BarChart3, 
  TrendingUp, 
  Activity, 
  Download, 
  Settings, 
  Calendar as CalendarIcon,
  Filter,
  RefreshCw,
  Bell,
  Share2,
  Database,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Users,
  Shield,
  Zap,
  ArrowLeft,
  LogOut,
  Building2,
  Layers
} from 'lucide-react';
import CredentialUtilizationReport from './CredentialUtilizationReport';
import PolicyTrendsReport from './PolicyTrendsReport';
import AgentActivityReport from './AgentActivityReport';
import { AnalyticsTimeRange, AnalyticsSummary, ExportOptions, SyncDestination } from '../../types/AnalyticsTypes';
import { useAnalytics, PlatformAnalytics, OrganizationAnalytics } from '../../hooks/useAnalytics';

interface AnalyticsDashboardProps {
  userRole: 'super_admin' | 'admin' | 'verifier';
}

export default function AnalyticsDashboard({ userRole }: AnalyticsDashboardProps) {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const { platformData, organizationData, isLoading, error, refreshData, getOrganizationAnalytics } = useAnalytics();
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState<AnalyticsTimeRange>({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    endDate: new Date().toISOString(),
    period: 'daily'
  });
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);

  // Transform real API data to match existing AnalyticsSummary interface
  const transformPlatformData = (data: PlatformAnalytics): AnalyticsSummary => {
    return {
      dateRange,
      credentialMetrics: {
        totalCredentials: data.total_credentials,
        activeCredentials: data.total_credentials, // Assuming all are active for now
        expiredCredentials: 0, // Not available in current API
        revokedCredentials: 0, // Not available in current API
        issuanceRate: { daily: 0, weekly: 0, monthly: 0 }, // Not available in current API
        verificationRate: { daily: 0, weekly: 0, monthly: 0 }, // Not available in current API
        topIssuers: data.organizations.slice(0, 3).map((org, index) => ({
          organizationId: org.org_id,
          organizationName: org.org_name,
          credentialsIssued: org.credential_count,
          percentage: (org.credential_count / data.total_credentials) * 100
        })),
        topVerifiers: [], // Not available in current API
        credentialsByType: [], // Not available in current API
        utilizationByIndustry: [] // Not available in current API
      },
      policyMetrics: {
        totalPolicyEvaluations: 0, // Not available in current API
        passRate: 0,
        failRate: 0,
        trendsOverTime: [],
        policyPerformance: [],
        failureReasons: [],
        complianceByIndustry: []
      },
      agentMetrics: {
        totalAgents: 0, // Not available in current API
        activeAgents: 0,
        totalInteractions: 0,
        averageResponseTime: 0,
        activityByAgent: [],
        interactionTrends: [],
        geographicUsage: [],
        peakUsageHours: []
      },
      keyInsights: [
        {
          type: 'trend',
          title: 'Platform Growth',
          description: `Platform has ${data.total_organizations} organizations with ${data.total_users} users`,
          impact: 'medium',
          actionable: true,
          recommendation: 'Monitor platform growth and scale infrastructure accordingly'
        }
      ],
      systemHealth: {
        overallScore: 85,
        credentialScore: 90,
        policyScore: 80,
        agentScore: 80,
        alerts: []
      }
    };
  };

  useEffect(() => {
    if (platformData) {
      setSummary(transformPlatformData(platformData));
    }
  }, [platformData, dateRange]);

  const handleRefresh = async () => {
    await refreshData();
    setLastUpdated(new Date());
  };

  const handleExport = (options: ExportOptions) => {
    // Implementation for exporting data
    console.log('Exporting with options:', options);
  };

  const handleBackToDashboard = () => {
    // Navigate back to role-specific dashboard instead of generic /dashboard
    const roleRoute = getRoleRoute(user?.role || 'holder');
    navigate(`/${roleRoute}`);
  };

  const getRoleRoute = (role: string): string => {
    switch (role) {
      case 'super_admin':
      case 'admin':
        return 'admin';
      case 'issuer':
        return 'issuer';
      case 'holder':
        return 'recipient';
      case 'verifier':
        return 'verifier';
      default:
        return 'recipient';
    }
  };

  const getHealthScoreColor = (score: number) => {
    if (score >= 90) return 'text-verified-green';
    if (score >= 80) return 'text-amber-500';
    if (score >= 70) return 'text-orange-500';
    return 'text-red-500';
  };

  const getHealthScoreBg = (score: number) => {
    if (score >= 90) return 'bg-verified-green';
    if (score >= 80) return 'bg-amber-500';
    if (score >= 70) return 'bg-orange-500';
    return 'bg-red-500';
  };

  if (!summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-electric-steel mx-auto mb-4" />
          <p className="text-warm-slate">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cool-gray-100">
      {/* Header */}
      <header className="credential-capital-header shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBackToDashboard}
                className="credential-capital-button-header"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div className="bg-white/10 p-2 rounded-lg">
                <BarChart3 className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-display font-bold text-white">
                  Analytics & Reports
                </h1>
                <div className="flex items-center space-x-2">
                  <p className="text-blue-100">
                    {user?.name}
                  </p>
                  {tenant && (
                    <Badge variant="outline" className="text-xs border-white/20 text-blue-100">
                      <Building2 className="h-3 w-3 mr-1" />
                      {tenant.name}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant="outline" className="text-xs border-white/20 text-blue-100">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isLoading}
                className="credential-capital-button-header"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button size="sm" className="bg-white text-midnight-indigo hover:bg-gray-50">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={logout}
                className="credential-capital-button-header"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="w-full max-w-7xl mx-auto p-6 space-y-6">
        {/* Error Display */}
        {error && (
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && !summary && (
          <div className="flex items-center justify-center p-12">
            <div className="text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-electric-steel" />
              <p className="text-warm-slate">Loading analytics data...</p>
            </div>
          </div>
        )}

        {/* System Health Overview */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="credential-capital-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-warm-slate">Overall Health</p>
                  <p className={`text-2xl font-semibold ${getHealthScoreColor(summary.systemHealth.overallScore)}`}>
                    {summary.systemHealth.overallScore}%
                  </p>
                </div>
                <div className={`p-3 rounded-full ${getHealthScoreBg(summary.systemHealth.overallScore)}/10`}>
                  <BarChart3 className={`h-6 w-6 ${getHealthScoreColor(summary.systemHealth.overallScore)}`} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="credential-capital-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-warm-slate">Credentials</p>
                  <p className={`text-2xl font-semibold ${getHealthScoreColor(summary.systemHealth.credentialScore)}`}>
                    {summary.systemHealth.credentialScore}%
                  </p>
                </div>
                <div className="p-3 rounded-full bg-electric-steel/10">
                  <Shield className="h-6 w-6 text-electric-steel" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="credential-capital-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-warm-slate">Policies</p>
                  <p className={`text-2xl font-semibold ${getHealthScoreColor(summary.systemHealth.policyScore)}`}>
                    {summary.systemHealth.policyScore}%
                  </p>
                </div>
                <div className="p-3 rounded-full bg-electric-steel/10">
                  <CheckCircle2 className="h-6 w-6 text-electric-steel" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="credential-capital-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-warm-slate">Agents</p>
                  <p className={`text-2xl font-semibold ${getHealthScoreColor(summary.systemHealth.agentScore)}`}>
                    {summary.systemHealth.agentScore}%
                  </p>
                </div>
                <div className="p-3 rounded-full bg-electric-steel/10">
                  <Zap className="h-6 w-6 text-electric-steel" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        )}

        {/* Key Insights */}
        {summary && summary.keyInsights.length > 0 && (
          <Card className="credential-capital-card">
            <CardHeader>
              <CardTitle className="text-lg font-display text-charcoal-ink">Key Insights</CardTitle>
              <CardDescription>
                Important trends and recommendations based on your data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {summary.keyInsights.map((insight, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-l-4 ${
                      insight.type === 'alert' ? 'border-red-500 bg-red-50' :
                      insight.type === 'opportunity' ? 'border-verified-green bg-green-50' :
                      insight.type === 'trend' ? 'border-electric-steel bg-blue-50' :
                      'border-amber-500 bg-amber-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-charcoal-ink">{insight.title}</h4>
                      <Badge
                        variant="outline"
                        className={`text-xs ${
                          insight.impact === 'high' ? 'border-red-500 text-red-700' :
                          insight.impact === 'medium' ? 'border-amber-500 text-amber-700' :
                          'border-gray-500 text-gray-700'
                        }`}
                      >
                        {insight.impact} impact
                      </Badge>
                    </div>
                    <p className="text-sm text-warm-slate mb-3">{insight.description}</p>
                    {insight.recommendation && (
                      <p className="text-sm font-medium text-charcoal-ink">
                        💡 {insight.recommendation}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Analytics Tabs */}
        {summary && (
          <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="credentials">Credentials</TabsTrigger>
                <TabsTrigger value="policies">Policies</TabsTrigger>
                <TabsTrigger value="agents">Agents</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6 mt-6">
                {/* Platform Overview - Real Data */}
                {platformData && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-charcoal-ink">Platform Overview</h3>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="p-4 bg-cool-gray-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Building2 className="h-5 w-5 text-electric-steel" />
                          <span className="text-sm text-warm-slate">Organizations</span>
                        </div>
                        <p className="text-xl font-semibold text-charcoal-ink mt-1">
                          {platformData.total_organizations.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-4 bg-cool-gray-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Users className="h-5 w-5 text-electric-steel" />
                          <span className="text-sm text-warm-slate">Total Users</span>
                        </div>
                        <p className="text-xl font-semibold text-charcoal-ink mt-1">
                          {platformData.total_users.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-4 bg-cool-gray-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Shield className="h-5 w-5 text-verified-green" />
                          <span className="text-sm text-warm-slate">Credentials</span>
                        </div>
                        <p className="text-xl font-semibold text-charcoal-ink mt-1">
                          {platformData.total_credentials.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-4 bg-cool-gray-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Layers className="h-5 w-5 text-electric-steel" />
                          <span className="text-sm text-warm-slate">Templates</span>
                        </div>
                        <p className="text-xl font-semibold text-charcoal-ink mt-1">
                          {platformData.total_templates.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Quick Stats */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-charcoal-ink">Quick Statistics</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-cool-gray-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Users className="h-5 w-5 text-electric-steel" />
                          <span className="text-sm text-warm-slate">Total Credentials</span>
                        </div>
                        <p className="text-xl font-semibold text-charcoal-ink mt-1">
                          {summary.credentialMetrics.totalCredentials.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-4 bg-cool-gray-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <TrendingUp className="h-5 w-5 text-verified-green" />
                          <span className="text-sm text-warm-slate">Policy Pass Rate</span>
                        </div>
                        <p className="text-xl font-semibold text-charcoal-ink mt-1">
                          {summary.policyMetrics.passRate}%
                        </p>
                      </div>
                      <div className="p-4 bg-cool-gray-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Activity className="h-5 w-5 text-electric-steel" />
                          <span className="text-sm text-warm-slate">Active Agents</span>
                        </div>
                        <p className="text-xl font-semibold text-charcoal-ink mt-1">
                          {summary.agentMetrics.activeAgents}
                        </p>
                      </div>
                      <div className="p-4 bg-cool-gray-100 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <Clock className="h-5 w-5 text-electric-steel" />
                          <span className="text-sm text-warm-slate">Avg Response Time</span>
                        </div>
                        <p className="text-xl font-semibold text-charcoal-ink mt-1">
                          {summary.agentMetrics.averageResponseTime}s
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* System Alerts */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-charcoal-ink">System Alerts</h3>
                    <div className="space-y-2">
                      {summary.systemHealth.alerts.map((alert, index) => (
                        <div
                          key={index}
                          className={`flex items-center space-x-3 p-3 rounded-lg ${
                            alert.severity === 'critical' ? 'bg-red-50 border border-red-200' :
                            alert.severity === 'error' ? 'bg-red-50 border border-red-200' :
                            alert.severity === 'warning' ? 'bg-amber-50 border border-amber-200' :
                            'bg-blue-50 border border-blue-200'
                          }`}
                        >
                          <AlertTriangle
                            className={`h-5 w-5 ${
                              alert.severity === 'critical' ? 'text-red-600' :
                              alert.severity === 'error' ? 'text-red-600' :
                              alert.severity === 'warning' ? 'text-amber-600' :
                              'text-blue-600'
                            }`}
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-charcoal-ink">{alert.message}</p>
                            <p className="text-xs text-warm-slate capitalize">{alert.component} component</p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {alert.severity}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="credentials" className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-ink">Credential Utilization Report</h3>
                    <p className="text-sm text-warm-slate">Detailed analysis of credential usage patterns</p>
                  </div>
                  <span className="text-red-600 font-semibold text-sm bg-red-50 px-3 py-1 rounded-full border border-red-200">Coming Soon</span>
                </div>
                <CredentialUtilizationReport 
                  data={summary.credentialMetrics}
                  dateRange={dateRange}
                  onExport={handleExport}
                />
              </TabsContent>

              <TabsContent value="policies" className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-ink">Policy Trends Report</h3>
                    <p className="text-sm text-warm-slate">Analysis of policy performance and trends</p>
                  </div>
                  <span className="text-red-600 font-semibold text-sm bg-red-50 px-3 py-1 rounded-full border border-red-200">Coming Soon</span>
                </div>
                <PolicyTrendsReport 
                  data={summary.policyMetrics}
                  dateRange={dateRange}
                  onExport={handleExport}
                />
              </TabsContent>

              <TabsContent value="agents" className="mt-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-charcoal-ink">Agent Activity Report</h3>
                    <p className="text-sm text-warm-slate">Monitor agent performance and activity</p>
                  </div>
                  <span className="text-red-600 font-semibold text-sm bg-red-50 px-3 py-1 rounded-full border border-red-200">Coming Soon</span>
                </div>
                <AgentActivityReport 
                  data={summary.agentMetrics}
                  dateRange={dateRange}
                  onExport={handleExport}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        )}
      </div>
    </div>
  );
}